// RunProgressPanelV2 — V3 right-rail panel that combines:
//   1. "Execução ativa" header with overall progress (powered by the
//      todoList tool event the agent emits) and elapsed/remaining time
//   2. TaskListV2 — the planned-tasks list, current task highlighted
//   3. SubAgentBox — sub-agent sub-conversations (so the chat surface
//      stays focused on the main response)
//   4. AgentActivityV2 — friendly streaming timeline of tool calls,
//      newest-first, derived from the live interaction
//   5. Pausar / Cancelar footer (consumer wires the actual handlers)
//
// Collapse:
//   The whole panel can collapse into a 60px rail with five at-a-glance
//   indicators stacked top-to-bottom: an expand handle (PanelIcon), the
//   connection-state dot, a circular task-progress arc with % inside,
//   one tile per active tool category (icon + count badge), and a
//   tokens/s readout pinned to the bottom. Each rail tile has a rich
//   Tooltip — the task arc surfaces a mini TaskListV2; each tool tile
//   lists its calls via `getFriendlyLabel` — so the user can drill in
//   without expanding the panel.

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Box from '@mui/material/Box';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import { useTheme } from '@mui/material/styles';
import { useAiChatServices, useAiChatSessionStore } from '../AiChat/AiChatContext';
import { ClientMessage } from '../../models/ClientMessage';
import type { TodoList } from '../../models/todoListModel';
import { TaskListV2 } from './TaskListV2';
import { AgentActivityV2 } from './AgentActivityV2';
import { LockClosedIcon, LockOpenIcon, PanelIcon } from './icons';
import CloseIcon from '@mui/icons-material/Close';
import { SubAgentBoxV2 } from './SubAgentBoxV2';
import { AgentGatewayWSAsync } from '../../services/servers/AgentGatewayWSAsync';
import { ConnectionState } from '../../models/connectionState';
import StreamStatistics from '../AiChat/ChatPane/StreamStatistics';
import { InteractionPartUtils, type InteractionPart } from '../../models/InteractionPart';
import { getFriendlyLabel, type FriendlyState } from '../AiChat/ChatPane/friendlyToolConfig';
import { TOOL_CATEGORIES, getToolCategory } from './toolCategories';

const pulseKeyframes = `@keyframes v2rppulse{0%,100%{opacity:1}50%{opacity:.3}}`;
const spinKeyframes = `@keyframes v2rpspin{to{transform:rotate(360deg)}}`;

const PANEL_WIDTH = '10%';
const RAIL_WIDTH = 45;

/**
 * Subcard wrapper used for the Tasks / Activity sections inside the
 * RunProgressPanel. Matches the v2 design language: rounded box on the
 * canvas (`bg0`) with a soft border, a small uppercase title strip at
 * the top, and the section content below.
 */
const SectionCard: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
    <Box
        sx={(theme) => ({
            backgroundColor: theme.palette.background.default,
            border: `1px solid ${theme.palette.border.soft}`,
            borderRadius: `${theme.shape.borderRadius}px`,
            overflow: 'hidden',
        })}
    >
        <Box sx={{ px: 1.5, pt: 1.25, pb: 0.75 }}>
            <Typography
                sx={(theme) => ({
                    fontSize: 10.5,
                    fontWeight: 500,
                    color: theme.palette.text.secondary,
                    textTransform: 'uppercase',
                    letterSpacing: 0.6,
                })}
            >
                {title}
            </Typography>
        </Box>
        <Box sx={{ px: 1, pb: 1 }}>{children}</Box>
    </Box>
);

export interface RunProgressPanelV2Props {
    /** Width of the panel when expanded. Defaults to 20vw. Accepts any CSS width value. */
    width?: number | string;
    /** Title shown in the header. */
    title?: string;
    /**
     * Optional override for the Pause action. By default the panel calls
     * `agentGatewayService.suspend()` on the gateway it pulls from
     * `useAiChatServices()`. Pass `null` to hide the button entirely.
     */
    onPause?: (() => void) | null;
    /**
     * Optional override for the Cancel action. By default the panel calls
     * `agentGatewayService.stopStream(true)` to terminate the active turn.
     * Pass `null` to hide the button entirely.
     */
    onCancel?: (() => void) | null;
    /** Hide reasoning chunks in the activity timeline. Defaults to `true`. */
    hideReasoning?: boolean;
    /** Controlled collapsed state. When `undefined` the panel manages it itself. */
    collapsed?: boolean;
    /** Called when the user toggles collapse. */
    onCollapsedChange?: (collapsed: boolean) => void;
    /** Default collapsed state when uncontrolled. */
    defaultCollapsed?: boolean;
}

/**
 * Live `MM:SS decorridos` clock — subscribes directly to
 * `statistics?.elapsed` (the WS pushes this value as part of every
 * stats packet, already throttled to 120ms). Mounting it as a memoised
 * leaf means the parent panel doesn't redraw on every push; only this
 * one `<Typography>` re-renders when the elapsed seconds actually
 * change. When the turn ends and stats stop arriving, the value
 * naturally freezes — no setInterval to clean up, no idle ticking.
 */
const ElapsedClock: React.FC = React.memo(() => {
    const elapsedSec = useAiChatSessionStore((s) => s.statistics?.elapsed ?? 0);
    return (
        <Typography sx={(theme) => ({ fontSize: 10.5, color: theme.palette.text.secondary, fontFamily: theme.typography.fontFamilyMono })}>
            {formatElapsed(elapsedSec * 1000)} decorridos
        </Typography>
    );
});
ElapsedClock.displayName = 'ElapsedClock';

// Shared sx for tooltip popper surfaces on the collapsed rail. The
// default MUI tooltip is a dark mini-pill; for the rail we want a
// full-bleed v2 panel (raised bg, soft border) so the embedded
// TaskListV2 / friendly-label rows read like the rest of the chrome.
const railTooltipSlotProps = {
    tooltip: {
        sx: (theme: any) => ({
            backgroundColor: theme.palette.surface.raised,
            border: `1px solid ${theme.palette.divider}`,
            color: theme.palette.text.primary,
            maxWidth: 'none',
            p: 0,
            boxShadow: theme.shadows[6],
        }),
    },
} as const;

function friendlyStateForCall(part: InteractionPart): FriendlyState {
    const status = part.functionDetails?.status;
    if (status === 'Done') return 'response';
    if (status === 'Composing') return 'partial';
    return 'call';
}

function callLooksLikeError(part: InteractionPart): boolean {
    const fn = part.functionDetails;
    if (!fn) return false;
    if (fn.status !== 'Done') return false;
    const resp = fn.response;
    return typeof resp === 'string' && /error|exception|fail/i.test(resp);
}

// V2 byte-bucket dimensions — kept identical to AgentActivityV2's
// `V2ByteBuckets` so the texture matches the expanded panel. The only
// rail-specific bit is the wrapper around them (overflow + right-align).
const BUCKET_W = 4;
const BUCKET_H = 8;
const BYTES_PER_BUCKET = 500;
const MAX_VISIBLE_BUCKETS = 12;

/**
 * Bucket strip used in the per-tile "composing" footer. Visually
 * identical to AgentActivityV2's `V2ByteBuckets` (same 4×8 cells,
 * same 500-bytes-per-bucket chunking, same partial-fill pulse) — the
 * rail-specific wrapper around it sets `overflow: hidden` +
 * `justifyContent: flex-end` so once more than ~4 buckets accumulate
 * the older ones clip off the left edge while the newest stay pinned
 * to the right inside the 30px tile.
 *
 * `compact` shrinks the gap between bars (and tightens the bar border)
 * so more buckets fit in the same width — used inside the rail's
 * narrow tiles. Default is the full-padding visual that matches the
 * expanded panel.
 */
const RailBuckets: React.FC<{ bytes: number; compact?: boolean }> = React.memo(({ bytes, compact = false }) => {
    if (bytes <= 0) return null;
    const fullCount = Math.floor(bytes / BYTES_PER_BUCKET);
    const partialFill = (bytes % BYTES_PER_BUCKET) / BYTES_PER_BUCKET;
    const visible = Math.min(MAX_VISIBLE_BUCKETS, fullCount + (partialFill > 0 ? 1 : 0)) || 1;
    const gap = compact ? '0px' : '2px';
    return (
        <Box
            sx={{
                width: '90%',
                overflow: 'hidden',
                display: 'flex',
                justifyContent: 'flex-end',
                height: BUCKET_H + 2,
            }}
            aria-hidden
        >
            <Box
                sx={{
                    display: 'inline-flex',
                    alignItems: 'flex-end',
                    gap,
                    flexShrink: 0,
                }}
            >
                {Array.from({ length: visible }, (_, i) => {
                    const isFull = i < fullCount;
                    const isActive = !isFull && i === fullCount && partialFill > 0;
                    const fillRatio = isFull ? 1 : isActive ? partialFill : 0;
                    return (
                        <Box
                            key={i}
                            sx={(theme) => ({
                                position: 'relative',
                                width: `${BUCKET_W}px`,
                                height: `${BUCKET_H}px`,
                                borderRadius: '1px',
                                border: `1px solid ${theme.palette.border.soft}`,
                                backgroundColor: theme.palette.surface.raised,
                                overflow: 'hidden',
                                flexShrink: 0,
                            })}
                        >
                            <Box
                                sx={(theme) => ({
                                    position: 'absolute',
                                    left: 0,
                                    right: 0,
                                    bottom: 0,
                                    height: `${fillRatio * 100}%`,
                                    backgroundColor: theme.palette.primary.main,
                                    opacity: isFull ? 0.85 : 0.7,
                                    transition: 'height .15s ease-out',
                                    animation: isActive ? 'v2rppulse 0.9s ease-in-out infinite' : 'none',
                                })}
                            />
                        </Box>
                    );
                })}
            </Box>
        </Box>
    );
});
RailBuckets.displayName = 'RailBuckets';

/**
 * Live mm:ss / s readout for the per-tile "executing" footer. Owns a
 * 1Hz `setInterval` that only ticks while mounted — i.e. only while
 * the call is in the `Executing` state — so the cost is one timer per
 * concurrently-running tool (almost always 0 or 1).
 */
const RailCallTimer: React.FC<{ start: Date }> = React.memo(({ start }) => {
    const [, force] = useState(0);
    useEffect(() => {
        const t = setInterval(() => force((x) => (x + 1) % 1024), 1000);
        return () => clearInterval(t);
    }, []);
    const sec = Math.max(0, Math.floor((Date.now() - start.getTime()) / 1000));
    const m = Math.floor(sec / 60);
    const s = sec % 60;
    const display = m > 0 ? `${m}:${s.toString().padStart(2, '0')}` : `${s}s`;
    return (
        <Box
            component="span"
            sx={(theme) => ({
                fontFamily: theme.typography.fontFamilyMono,
                fontSize: 8.5,
                lineHeight: 1,
                color: theme.palette.text.primary,
            })}
        >
            {display}
        </Box>
    );
});
RailCallTimer.displayName = 'RailCallTimer';

/**
 * Per-tool rail tile — three visual stages tied to the call's status:
 *   1. Composing → open lock on top, category icon, buckets footer
 *      (bars fill as args stream in, mirrors AgentActivityV2's bucket
 *      indicator).
 *   2. Executing → open lock on top (pulsing), category icon, live
 *      mm:ss timer footer driven by `functionDetails.start`.
 *   3. Done → closed lock on top, category icon. No footer — the
 *      closed lock is the "result is final" signal.
 */
const RailToolTile: React.FC<{ call: InteractionPart }> = ({ call }) => {
    const fn = call.functionDetails;
    if (!fn?.name) return null;
    const catId = getToolCategory(fn.name);
    const meta = TOOL_CATEGORIES[catId];
    const CategoryIcon = meta.Icon;
    const isComposing =
        fn.status === 'Composing' || call.eventType === 'partial_function_call';
    const isExecuting = fn.status === 'Executing';
    const isDone = fn.status === 'Done';
    const isError = callLooksLikeError(call);
    const state = friendlyStateForCall(call);
    const label = getFriendlyLabel(fn.name, state, fn.args ?? fn.text, isError);

    // Streamed-byte counter for the bucket texture — only meaningful
    // while the args are still composing. Matches AgentActivityV2's
    // source: `getPartTextContent` walks `part.textDetails.textParts`
    // (one chunk per `StreamingFunctionCallUpdateContent` event), which
    // accumulates the full payload — `fn.text` alone only carries the
    // latest chunk. Computed fresh on every render so in-place growth
    // of `textParts` is picked up immediately (no `useMemo` because the
    // part object can be ref-stable while its contents mutate).
    const streamedBytes = (() => {
        const accumulated = InteractionPartUtils.getPartTextContent(call);
        if (typeof accumulated === 'string' && accumulated.length > 0) return accumulated.length;
        if (typeof fn.text === 'string' && fn.text.length > 0) return fn.text.length;
        if (typeof fn.args === 'string') return fn.args.length;
        if (fn.args != null) {
            try {
                return JSON.stringify(fn.args).length;
            } catch {
                return 0;
            }
        }
        return 0;
    })();

    return (
        <Tooltip title={label} placement="left">
            <Box
                sx={(theme) => {
                    const accent = isError
                        ? theme.palette.secondary.main
                        : isExecuting
                            ? theme.palette.primary.main
                            : isDone
                                ? theme.palette.success.main
                                : theme.palette.text.disabled;
                    const accentBorder = isExecuting || isComposing || isError;
                    return {
                        width: 30,
                        maxHeight: "50px",
                        minHeight: "40px",
                        paddingTop: 0,
                        paddingBottom: 0.5,
                        borderRadius: 0.875,
                        backgroundColor: theme.palette.surface.raised,
                        border: `1px solid ${accentBorder ? accent : theme.palette.divider}`,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '0px',
                        position:'relative',
                        color: isDone ? theme.palette.text.secondary : theme.palette.text.primary,
                        flexShrink: 0,
                        transition: 'border-color .2s ease, color .2s ease',
                    };
                }}
            >
                {/* Lock — open while in-flight, closed once Done. Pulses
                    softly during Executing to draw the eye to the
                    currently-running tool. */}
                <Box
                    sx={(theme) => ({
                        color: isError
                            ? theme.palette.secondary.main
                            : isDone
                                ? theme.palette.success.main
                                : isExecuting
                                    ? theme.palette.primary.main
                                    : theme.palette.text.disabled,
                        display: 'flex',
                        position:'relative',
                        top:'3px',
                        right: '-4px',
                        animation: isExecuting ? 'v2rppulse 1.5s ease-in-out infinite' : 'none',
                    })}
                >
                    {isDone ? <LockClosedIcon size={10} /> : <LockOpenIcon size={10} />}
                </Box>
                {/* Category icon — strokes inherit the tile's `color` so
                    Done tiles read as muted while in-flight ones stay
                    at full text contrast. */}
                <CategoryIcon size={14} />
                {/* Stage-specific footer: buckets while composing, live
                    timer while executing, nothing once Done. */}
                {isComposing && <RailBuckets bytes={streamedBytes} compact />}
                {isExecuting && fn.start && <RailCallTimer start={fn.start} />}
            </Box>
        </Tooltip>
    );
};
RailToolTile.displayName = 'RailToolTile';

/**
 * Leaf for the collapsed-rail tool list. Subscribes to `currentMessage`
 * and `messages` *as references* (matching AgentActivityV2's pattern),
 * then derives the filtered call list with `useMemo`.
 *
 * The filter must include `function_response` alongside `function_call`
 * / `partial_function_call`: when a tool response arrives the runtime
 * mutates the original `function_call` part's `eventType` to
 * `function_response` in place (see `interactionService.ts:
 * mergeCallResponseParts`) and merges the response into its
 * `functionDetails`. Excluding `function_response` here is what makes
 * Done tiles vanish — the part is still the same object carrying the
 * same status/start/end timestamps, just relabelled. Including it keeps
 * completed calls in the chronological list, matching the expanded
 * AgentActivityV2 timeline.
 *
 * We deliberately do NOT use a `useShallow` selector that returns the
 * filtered array directly: when a part mutates in place, useShallow
 * sees the same item refs and short-circuits, leaving the rendered
 * status stale. Subscribing to the interaction refs and deriving
 * downstream guarantees we re-render on every store update broadcast
 * the same way AgentActivityV2 does.
 */
const ToolCallList: React.FC = React.memo(() => {
    const currentMessage = useAiChatSessionStore((s) => s.currentMessage);
    const turnOpen = useAiChatSessionStore((s) => s.turnOpen);

    const calls = useMemo(() => {
        // Once the turn closes the rail's tool history clears — matches
        // the "no live activity to show" rule used by AgentActivityV2
        // when no turn pill is selected. We only read `currentMessage`
        // (not the last completed `messages[…]` fallback) so stale
        // tiles never leak from a previous turn either.
        if (!turnOpen || !currentMessage?.parts) return [] as InteractionPart[];
        return currentMessage.parts.filter(
            (p) =>
                (p.eventType === 'function_call' ||
                    p.eventType === 'partial_function_call' ||
                    p.eventType === 'function_response') &&
                !p.functionDetails?.silent &&
                !!p.functionDetails?.name,
        );
    }, [currentMessage, turnOpen]);

    if (calls.length === 0) return null;

    return (
        <>
            {calls.map((c, i) => (
                <RailToolTile key={c.functionDetails?.id ?? `tile-${i}`} call={c} />
            ))}
        </>
    );
});
ToolCallList.displayName = 'ToolCallList';

/**
 * Leaf for the always-visible tokens-per-second chip at the bottom of
 * the collapsed rail. Subscribes only to `statistics?.tokensPerSecond`
 * so the rest of the panel doesn't redraw on every stats packet.
 */
const RailTokensPerSecond: React.FC = React.memo(() => {
    const tps = useAiChatSessionStore((s) => s.statistics?.tokensPerSecond ?? 0);
    const display = tps > 0 ? tps.toFixed(tps >= 10 ? 1 : 2) : '—';
    return (
        <Tooltip title={`${tps.toFixed(2)} tokens/s`} placement="left">
            <Box
                sx={(theme) => ({
                    width: 30,
                    minHeight: 30,
                    borderRadius: 0.875,
                    backgroundColor: theme.palette.surface.raised,
                    border: `1px solid ${theme.palette.divider}`,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    py: 0.25,
                })}
            >
                <Box
                    sx={(theme) => ({
                        fontFamily: theme.typography.fontFamilyMono,
                        fontSize: 9.5,
                        fontWeight: 600,
                        color: theme.palette.text.primary,
                        lineHeight: 1,
                    })}
                >
                    {display}
                </Box>
                <Box
                    sx={(theme) => ({
                        fontSize: 7.5,
                        color: theme.palette.text.disabled,
                        lineHeight: 1,
                        mt: '2px',
                        letterSpacing: 0.2,
                    })}
                >
                    tok/s
                </Box>
            </Box>
        </Tooltip>
    );
});
RailTokensPerSecond.displayName = 'RailTokensPerSecond';

/**
 * Collapsed-rail task tile — a circular progress arc (track + filled
 * arc, like SessionUsageBadge) with the integer percentage rendered in
 * the centre. Hover surfaces a `TaskListV2` mini-list with the full
 * todo list so the user can read individual tasks without expanding
 * the panel.
 */
const RailTaskProgress: React.FC<{
    todoList: TodoList | null;
    completed: number;
    total: number;
    pct: number;
}> = ({ todoList, completed, total, pct }) => {
    const theme = useTheme();
    const SIZE = 30;
    const STROKE = 2.5;
    const RADIUS = (SIZE - STROKE) / 2;
    const CIRC = 2 * Math.PI * RADIUS;
    const clamped = Math.max(0, Math.min(1, pct));
    const filled = CIRC * clamped;
    const integerPct = Math.round(clamped * 100);

    const tooltipBody = todoList ? (
        <Box sx={{ width: 280 }}>
            <Box
                sx={(theme) => ({
                    px: 1.25,
                    py: 0.75,
                    borderBottom: `1px solid ${theme.palette.border.soft}`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 1,
                })}
            >
                <Box
                    sx={(theme) => ({
                        fontSize: 10,
                        fontWeight: 500,
                        textTransform: 'uppercase',
                        letterSpacing: 0.6,
                        color: theme.palette.text.secondary,
                    })}
                >
                    Tarefas do agente
                </Box>
                <Box
                    sx={(theme) => ({
                        fontFamily: theme.typography.fontFamilyMono,
                        fontSize: 10.5,
                        color: theme.palette.text.primary,
                    })}
                >
                    {completed}/{total}
                </Box>
            </Box>
            <Box sx={{ p: 0.5, maxHeight: 360, overflow: 'auto' }}>
                <TaskListV2 todoList={todoList} dense />
            </Box>
        </Box>
    ) : (
        `Tarefas: ${completed}/${total}`
    );

    return (
        <Tooltip
            title={tooltipBody}
            placement="left"
            slotProps={todoList ? railTooltipSlotProps : undefined}
        >
            <Box
                sx={{
                    position: 'relative',
                    width: SIZE,
                    height: SIZE,
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                }}
                aria-label={`Tarefas: ${integerPct}%`}
            >
                <Box
                    component="svg"
                    width={SIZE}
                    height={SIZE}
                    viewBox={`0 0 ${SIZE} ${SIZE}`}
                    sx={{
                        position: 'absolute',
                        inset: 0,
                        transform: 'rotate(-90deg)',
                    }}
                >
                    <circle
                        cx={SIZE / 2}
                        cy={SIZE / 2}
                        r={RADIUS}
                        fill="none"
                        stroke={theme.palette.surface.hover}
                        strokeWidth={STROKE}
                    />
                    <circle
                        cx={SIZE / 2}
                        cy={SIZE / 2}
                        r={RADIUS}
                        fill="none"
                        stroke={theme.palette.primary.main}
                        strokeWidth={STROKE}
                        strokeLinecap="round"
                        strokeDasharray={`${filled} ${CIRC}`}
                        style={{ transition: 'stroke-dasharray .25s ease' }}
                    />
                </Box>
                <Box
                    sx={(theme) => ({
                        position: 'relative',
                        fontFamily: theme.typography.fontFamilyMono,
                        fontSize: 9,
                        fontWeight: 600,
                        color: theme.palette.text.primary,
                        lineHeight: 1,
                    })}
                >
                    {integerPct}
                </Box>
            </Box>
        </Tooltip>
    );
};
RailTaskProgress.displayName = 'RailTaskProgress';

/**
 * Leaf for the sub-agent boxes block. Subscribes to `subAgents` only
 * here — the parent panel doesn't re-render when a sub-agent arrives
 * or finishes, just this list. SubAgentBoxV2 has its own internal
 * subscription so each box re-renders independently.
 */
const SubAgentList: React.FC = React.memo(() => {
    const subAgents = useAiChatSessionStore((s) => s.subAgents);
    const removeSubAgent = useAiChatSessionStore((s) => s.removeSubAgent);
    if (!subAgents || subAgents.size === 0) return null;
    const items = Array.from(subAgents.values());
    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {items.map((sa) => (
                <Box key={sa.id} sx={{ position: 'relative' }}>
                    <SubAgentBoxV2 subAgent={sa} defaultLevel={1} hideMessageLevel />
                    {sa.completed && (
                        <Tooltip title="Dispensar sub-agente">
                            <IconButton
                                size="small"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    removeSubAgent(sa.id);
                                }}
                                sx={(theme) => ({
                                    position: 'absolute',
                                    top: 4,
                                    right: 4,
                                    width: 18,
                                    height: 18,
                                    color: theme.palette.text.secondary,
                                    backgroundColor: theme.palette.surface.raised,
                                    border: `1px solid ${theme.palette.divider}`,
                                    '&:hover': {
                                        color: theme.palette.secondary.main,
                                        backgroundColor: theme.palette.surface.hover,
                                    },
                                })}
                            >
                                <CloseIcon sx={{ fontSize: 12 }} />
                            </IconButton>
                        </Tooltip>
                    )}
                </Box>
            ))}
        </Box>
    );
});
SubAgentList.displayName = 'SubAgentList';

function tryParseMainAgentTodoList(
    eventName: string | undefined,
    eventData: string | undefined,
    subAgentId: string | undefined,
): TodoList | null {
    // Only main-agent todo events drive the panel header progress.
    // Sub-agent events have their own SubAgentBox surface and must not
    // override the main agent's percentage / counters.
    if (!eventName || eventName !== 'todolistUpdated') return null;
    if (subAgentId) return null;
    if (!eventData) return null;
    try {
        return JSON.parse(eventData) as TodoList;
    } catch {
        return null;
    }
}

function formatElapsed(ms: number): string {
    if (ms <= 0) return '00:00';
    const totalSec = Math.floor(ms / 1000);
    const m = Math.floor(totalSec / 60).toString().padStart(2, '0');
    const s = (totalSec % 60).toString().padStart(2, '0');
    return `${m}:${s}`;
}

export const RunProgressPanelV2: React.FC<RunProgressPanelV2Props> = ({
    width = PANEL_WIDTH,
    title: _ignoredTitle,
    onPause,
    onCancel,
    hideReasoning = true,
    collapsed: collapsedProp,
    onCollapsedChange,
    defaultCollapsed = false,
}) => {
    void _ignoredTitle;
    const theme = useTheme();
    const [internalCollapsed, setInternalCollapsed] = useState(defaultCollapsed);
    const collapsed = collapsedProp ?? internalCollapsed;
    const setCollapsed = (next: boolean) => {
        if (collapsedProp === undefined) setInternalCollapsed(next);
        onCollapsedChange?.(next);
    };

    // Top-level subscriptions are restricted to the structural fields
    // — the ones that drive layout / chrome / progress bar. The
    // churn-y per-WS-packet fields (currentMessage, messages, subAgents)
    // are subscribed to inside leaf components below so that a packet
    // arriving only re-renders the leaf node it actually affects, not
    // the entire panel.
    const turnOpen = useAiChatSessionStore((s) => s.turnOpen);
    const lastToolEvent = useAiChatSessionStore((s) => s.lastToolEvent);
    const connectionStatus = useAiChatSessionStore((s) => s.connectionStatus);
    const { agentGatewayService } = useAiChatServices();

    useEffect(() => {
        setInternalCollapsed(defaultCollapsed);
    }, [defaultCollapsed])

    // Map ConnectionState → header dot color + label.
    const connectionDisplay = useMemo(() => {
        switch (connectionStatus) {
            case ConnectionState.Connected:
                return {
                    color: theme.palette.success.main,
                    label: turnOpen ? 'Conexão Ativa · Executando' : 'Conexão Ativa',
                    // Pulse always-on while the connection is up, even
                    // when no turn is in flight — the dot reads as a
                    // "heartbeat" indicator of the WS link, matching
                    // the user's mental model that "green + alive =
                    // pulsing". Other UI (progress arc, tool list,
                    // elapsed clock) already differentiate "running"
                    // from "idle".
                    pulse: true,
                };
            case ConnectionState.Connecting:
                return { color: theme.palette.primary.main, label: 'Conectando…', pulse: true };
            case ConnectionState.Disconnected:
                return { color: theme.palette.secondary.main, label: 'Desconectado', pulse: false };
            case ConnectionState.Idle:
            default:
                return { color: theme.palette.text.disabled, label: 'Sessão Inativa', pulse: false };
        }
    }, [connectionStatus, turnOpen, theme]);

    // Pause / continue / stop — handled internally by the panel using the
    // gateway service from the AiChat context. Suspend state lives in the
    // shared session store (`AiChatState.suspendState`) so PromptV2's
    // send-button cluster and this panel's header agree on the same
    // value: pausing in one surface flips both, sending a follow-up
    // resets both, the turn closing resets both. Hosts can still
    // override the handlers by passing custom ones, or hide them by
    // passing `null`.
    const suspendState = useAiChatSessionStore((s) => s.suspendState);
    const setSuspendState = useAiChatSessionStore((s) => s.setSuspendState);
    const isSuspending = suspendState === 'suspending';
    const isSuspended = suspendState === 'suspended';

    const defaultPause = useCallback(async () => {
        if (!(agentGatewayService instanceof AgentGatewayWSAsync)) return;
        if (isSuspended) {
            // "Continue" — send a canonical "Continue" message through
            // the gateway. `sendMessageInternal` clears its own
            // `suspendState` and starts a new interaction with that
            // text, which the agent receives as a user instruction to
            // resume from the pause point. The store's suspendState is
            // pre-cleared here so the cluster flips back to "idle"
            // immediately instead of waiting for the next turn-open
            // echo.
            //
            // NB: this used to call `agentGatewayService.cancelSuspend()`,
            // which is `turnComplete` in disguise — it FINALIZES the
            // partial output and closes the turn (i.e. the opposite of
            // "continue"). Bug surfaced when the pause control on the
            // panel was wired to that branch.
            setSuspendState('idle');
            try {
                await agentGatewayService.sendMessage(new ClientMessage('Continue', 'text/plain'));
            } catch (err) {
                console.error('Failed to send Continue message', err);
            }
            return;
        }
        try {
            setSuspendState('suspending');
            const ok = await agentGatewayService.suspend();
            setSuspendState(ok ? 'suspended' : 'idle');
        } catch (err) {
            console.error('Failed to suspend agent', err);
            setSuspendState('idle');
        }
    }, [agentGatewayService, isSuspended, setSuspendState]);

    const defaultCancel = useCallback(() => {
        if (!(agentGatewayService instanceof AgentGatewayWSAsync)) return;
        try {
            agentGatewayService.stopStream(true);
        } catch (err) {
            console.error('Failed to cancel turn', err);
        }
        setSuspendState('idle');
    }, [agentGatewayService, setSuspendState]);

    // Resolve the actual handlers — `undefined` means "use default", `null`
    // means "hide this button".
    const resolvedPause = onPause === null ? null : onPause ?? defaultPause;
    const resolvedCancel = onCancel === null ? null : onCancel ?? defaultCancel;

    const todoList = useMemo(
        () => tryParseMainAgentTodoList(
            lastToolEvent?.eventName,
            lastToolEvent?.eventData,
            lastToolEvent?.subAgentId,
        ),
        [lastToolEvent?.eventName, lastToolEvent?.eventData, lastToolEvent?.subAgentId],
    );

    const completed = todoList?.items.filter(
        (i) => String(i.status).toLowerCase() === 'completed',
    ).length ?? 0;
    const total = todoList?.items.length ?? 0;
    const pct = todoList?.percentComplete != null && Number.isFinite(todoList.percentComplete)
        ? Math.max(0, Math.min(1, todoList.percentComplete))
        : total > 0 ? completed / total : 0;

    if (collapsed) {
        return (
            <Box
                component="aside"
                onClick={() => setCollapsed(false)}
                sx={(theme) => ({
                    minWidth: RAIL_WIDTH,
                    display: 'inline-flex',
                    backgroundColor: theme.palette.background.default,
                    borderLeft: `1px solid ${theme.palette.divider}`,
                    flexDirection: 'column',
                    alignItems: 'center',
                    py: 1.25,
                    gap: 1.25,
                    height: '100%',
                    minHeight: 0,
                    overflow: 'hidden',
                    '& :hover': {
                        cursor: 'pointer'
                    }
                })}
            >
                <style>{`${pulseKeyframes}${spinKeyframes}`}</style>

                {/* Expand — uses PanelIcon (inverted via rotate(180deg) so
                    the inner divider sits on the right, reading as
                    "panel returns from the right edge"). Replaces the
                    earlier chevron, which read ambiguously next to the
                    other circular tiles. */}
                <Tooltip title="Expandir painel" placement="left">
                    <IconButton
                        size="small"
                        onClick={() => setCollapsed(false)}
                        sx={(theme) => ({
                            width: 30,
                            height: 30,
                            color: theme.palette.text.primary,
                            backgroundColor: theme.palette.surface.raised,
                            border: `1px solid ${theme.palette.divider}`,
                            borderRadius: 0.875,
                            transform: 'rotate(180deg)',
                            '&:hover': {
                                backgroundColor: theme.palette.surface.hover,
                                color: theme.palette.primary.main,
                            },
                        })}
                    >
                        <PanelIcon size={14} />
                    </IconButton>
                </Tooltip>

                {/* Connection indicator — filled dot + pulse for the WS
                    link (matches the expanded header), with a thin
                    spinning arc orbiting it whenever the agent has a
                    turn in flight. The arc reuses the rail's existing
                    `v2rpspin` keyframes; one side of the ring is
                    transparent so the rotation reads as a moving arc
                    rather than a static ring. */}
                <Tooltip title={connectionDisplay.label} placement="left">
                    <Box
                        sx={{
                            position: 'relative',
                            width: 30,
                            height: 30,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0,
                        }}
                    >
                        {turnOpen && (
                            <Box
                                aria-hidden
                                sx={{
                                    position: 'absolute',
                                    width: 22,
                                    height: 22,
                                    borderRadius: '50%',
                                    border: `2px solid ${connectionDisplay.color}`,
                                    borderTopColor: 'transparent',
                                    animation: 'v2rpspin 1.1s linear infinite',
                                }}
                            />
                        )}
                        <Box
                            sx={{
                                width: 10,
                                height: 10,
                                borderRadius: '50%',
                                backgroundColor: connectionDisplay.color,
                                animation: connectionDisplay.pulse
                                    ? 'v2rppulse 1.5s ease-in-out infinite'
                                    : 'none',
                            }}
                        />
                    </Box>
                </Tooltip>

                {/* Task progress — circular arc with the integer % in the
                    middle. Hover surfaces the full TaskListV2 in a
                    rich tooltip so individual tasks stay reachable
                    without expanding the panel. */}
                {total > 0 && (
                    <RailTaskProgress
                        todoList={todoList}
                        completed={completed}
                        total={total}
                        pct={pct}
                    />
                )}

                {/* Every tool call in the turn, stacked vertically.
                    Wrapped in a flex-1 scrollable column so the list
                    can grow with the turn without pushing the tokens
                    chip off the rail — when it overflows, the inner
                    container scrolls instead of the whole rail. The
                    scrollbar is hidden (too narrow to be useful at
                    60px) but mouse-wheel scrolling still works. */}
                <Box
                    sx={{
                        flex: 1,
                        minHeight: 0,
                        width: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: 1.25,
                        overflowY: 'auto',
                        overflowX: 'hidden',
                        py: 0.25,
                        scrollbarWidth: 'none',
                        '&::-webkit-scrollbar': { display: 'none' },
                    }}
                >
                    <ToolCallList />
                </Box>

                <RailTokensPerSecond />
            </Box>
        );
    }

    return (
        <Box
            component="aside"
            sx={(theme) => ({
                width: !collapsed ? width : '5%',
                maxWidth: '22%',
                minWidth: '22%',
                // flex: '0 0.1 3vw',
                backgroundColor: theme.palette.background.paper,
                borderLeft: `1px solid ${theme.palette.divider}`,
                display: 'inline-flex',
                flexDirection: 'column',
                minHeight: 0,
            })}
        >
            <style>{pulseKeyframes}</style>

            {/* Header */}
            <Box sx={(theme) => ({ p: 1.75, borderBottom: `1px solid ${theme.palette.divider}` })}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box
                        sx={{
                            width: 6,
                            height: 6,
                            borderRadius: '50%',
                            backgroundColor: connectionDisplay.color,
                            animation: connectionDisplay.pulse ? 'v2rppulse 1.5s ease-in-out infinite' : 'none',
                        }}
                    />
                    <Typography sx={(theme) => ({ fontSize: 12.5, fontWeight: 500, color: theme.palette.text.primary, flex: 1 })}>
                        {connectionDisplay.label}
                    </Typography>
                    {turnOpen && total > 0 && (
                        <Typography sx={(theme) => ({ fontSize: 10, color: theme.palette.text.disabled, fontFamily: theme.typography.fontFamilyMono })}>
                            {completed}/{total}
                        </Typography>
                    )}
                    <Tooltip title="Recolher painel">
                        <IconButton
                            size="small"
                            onClick={() => setCollapsed(true)}
                            sx={(theme) => ({ color: theme.palette.text.secondary, p: 0.25, '&:hover': { color: theme.palette.primary.main } })}
                        >
                            <PanelIcon size={13} />
                        </IconButton>
                    </Tooltip>
                </Box>
                {turnOpen && (
                    <>
                        <Box
                            sx={(theme) => ({
                                mt: 1.25,
                                height: 4,
                                backgroundColor: theme.palette.surface.hover,
                                borderRadius: 1,
                                overflow: 'hidden',
                            })}
                        >
                            <Box
                                sx={(theme) => ({
                                    width: `${Math.round(pct * 100)}%`,
                                    height: '100%',
                                    backgroundColor: theme.palette.primary.main,
                                    transition: 'width .25s ease',
                                })}
                            />
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.75 }}>
                            <ElapsedClock />
                            <Typography sx={(theme) => ({ fontSize: 10.5, color: theme.palette.text.secondary, fontFamily: theme.typography.fontFamilyMono })}>
                                em execução…
                            </Typography>
                        </Box>
                    </>
                )}
            </Box>

            {/* Body: tasks + sub-agents + activity, each in its own subcard.
                `minHeight: 0` is the unlock that lets `overflow: auto`
                kick in inside this nested flex column — without it the
                body stretches to fit content and the scroll never engages
                (TaskList + Activity just push the panel taller). */}
            <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto', p: 1.25, display: 'flex', flexDirection: 'column', gap: 1.25 }}>
                <SectionCard title="Tarefas do agente">
                    {todoList ? (
                        <TaskListV2 todoList={todoList} dense />
                    ) : (
                        <Typography
                            sx={(theme) => ({
                                fontSize: 11,
                                color: theme.palette.text.disabled,
                                fontStyle: 'italic',
                                px: 1,
                                py: 0.5,
                            })}
                        >
                            Nenhuma tarefa planejada ainda.
                        </Typography>
                    )}
                </SectionCard>

                <SubAgentList />

                <SectionCard title="Atividades">
                    <AgentActivityV2
                        hideReasoning={hideReasoning}
                        order="chronological"
                        showTurnHistory
                        emptyState={
                            <Typography
                                sx={(theme) => ({
                                    fontSize: 11,
                                    color: theme.palette.text.disabled,
                                    fontStyle: 'italic',
                                    px: 1,
                                    py: 0.5,
                                })}
                            >
                                {turnOpen
                                    ? 'Aguardando próxima ação do agente…'
                                    : 'Selecione um turno acima para ver as atividades.'}
                            </Typography>
                        }
                    />
                </SectionCard>
            </Box>

            {/* Connection / session-usage strip with the inline
                Pause/Stop control. StreamStatistics in compact mode
                renders mono stats on the left, the session-size circle
                badge in the middle, and our pause control on the right
                — replacing the previous separate footer + bar layout. */}
            {(resolvedPause || resolvedCancel) && (
                <Box
                    sx={(theme) => ({
                        borderTop: `1px solid ${theme.palette.divider}`,
                        backgroundColor: theme.palette.background.default,
                        px: 0.5,
                        py: 0.5,
                        '& .MuiTypography-caption': { color: theme.palette.text.secondary },
                    })}
                >
                    <StreamStatistics
                        compact
                        pauseControl={
                            (resolvedPause || resolvedCancel)
                                ? {
                                    turnOpen,
                                    suspended: isSuspended,
                                    suspending: isSuspending,
                                    onPause: resolvedPause ?? defaultPause,
                                    onCancel: resolvedCancel ?? defaultCancel,
                                }
                                : undefined
                        }
                    />
                </Box>
            )}
        </Box>
    );
};

RunProgressPanelV2.displayName = 'RunProgressPanelV2';
